'use client';

import themeVariables from '@/config/theme/themeVariable';
import CenterLayout from '@/layout/CenterLayout';
import { useRef, useState, useEffect } from 'react';
import NaverMap, { NaverMapHandle } from '@/components/NaverMap';
import { useLanguage } from '@/hooks/useLanguage';
import { useTranslation } from '@/hooks/useTranslation';
import { SupportedLanguage } from '@/config/store/globalStore';
import { useFetchRestaurants } from './_hook';
import { Restaurant } from '@/types/places';
import Link from 'next/link';
import { images } from '@/utils/images';
import { shortenAddress } from '@/utils/util';
import { Minimize2, Maximize2 } from 'lucide-react';
import { BlueRibbonLogos, MichelinLogos, PriceRangeBadge } from './_components/badges';
import FilterTab from './_components/FilterTab';
import RestaurantCard from './_components/RestaurantCard';
import { useAtom } from 'jotai';
import { viewTypeAtom, selectedPriceAtom, selectedCategoryAtom, selectedAwardAtom } from './_state';
import { STATIC_ENDPOINT } from '@/config/endpoint/endpoint';

interface RestaurantsClientPageProps {
  restaurantsData: Restaurant[];
  filtersData: any;
}

export default function RestaurantsClientPage({ restaurantsData, filtersData }: RestaurantsClientPageProps) {
  const mapRef = useRef<NaverMapHandle>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const { language } = useLanguage();
  const { t } = useTranslation();
  
  // FilterTab의 상태들을 사용
  const [viewType] = useAtom(viewTypeAtom);
  const [selectedPrice] = useAtom(selectedPriceAtom);
  const [selectedCategory] = useAtom(selectedCategoryAtom);
  const [selectedAward] = useAtom(selectedAwardAtom);

  // 필터 변경 시 클라이언트에서 데이터 패칭 (SSR 이후 필터 기능)
  const { data: filteredRestaurants, loading } = useFetchRestaurants({
    category: selectedCategory,
    price: selectedPrice,
    award: selectedAward
  });

  // 필터가 적용되지 않은 초기 상태일 때는 서버 데이터 사용, 필터 적용 시 클라이언트 데이터 사용
  const hasActiveFilters = selectedCategory || selectedPrice || selectedAward;
  const restaurants = hasActiveFilters ? filteredRestaurants : { result: restaurantsData };

  // 전체화면 모드일 때 페이지 스크롤 제어
  useEffect(() => {
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isFullscreen) {
        setIsFullscreen(false);
      }
    };

    if (isFullscreen) {
      document.addEventListener('keydown', handleEscKey);
    }

    return () => {
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [isFullscreen]);


  // 카드 클릭 핸들러
  const handleCardClick = (markerIndex: number) => {
    if (mapRef.current) {
      mapRef.current.focusOnMarker(markerIndex);
      const mapElement = document.querySelector('[data-map-container]');
      if (mapElement) {
        mapElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  };

  // 전체화면 토글 핸들러
  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };


  // 서버에서 필터링된 레스토랑 데이터를 마커 형태로 변환
  const markers = restaurants?.result?.map((place: Restaurant, index: number) => ({
    lat: place.coordinates.lat,
    lng: place.coordinates.lng,
    title: place.name[language as SupportedLanguage],
    description: (
      <div className="max-w-xs">
        <div className="flex items-center text-gray-600 text-sm gap-2 mb-2">
          <span className='text-gray-700'>{place.food_category[language as SupportedLanguage]}</span>
          <div className='flex items-center gap-1.5'>
            {place.blueribbon_info && (
              <BlueRibbonLogos count={place.blueribbon_info.ribbonType} />
            )}
            {place.michelin_info && (
              <MichelinLogos count={place.michelin_info.michelinAward} />
            )}
          </div>
        </div>
        <div className='text-[13px] text-gray-600 mb-3'>
          {place.address[language as SupportedLanguage]}
        </div>
        {/* 썸네일 이미지 4개 */}
        <div className="grid grid-cols-4 gap-1 mb-3">
          {place.images?.userImages?.slice(0,4)?.map((image: string, index: number) => (
            <img
              key={index}
              src={`${STATIC_ENDPOINT}/restaurants/${place.id}/userImages/${image}`}
              alt={`${place.name[language]}-user-image-${index + 1}`}
              className="w-16 h-16 rounded-md overflow-hidden object-cover"
            />
          ))}
        </div>

        {/* 자세히 확인하기 버튼 */}
        <Link className='h-full flex justify-center items-center cursor-pointer text-gray-600 text-sm p-2 rounded-lg hover:bg-gray-100 active:bg-gray-200 mt-3' href={`/${language}/places/restaurants/${place.id}`}>
          {t('detail_button')}
        </Link>
    </div>
    ),
    icon: (
      <div className={`w-8 h-8 rounded-full flex items-center justify-center shadow-lg border-2 border-white ${
        place.michelin_info ? 'bg-white' : 
        place.blueribbon_info ? 'bg-white' : 'bg-orange-500'
      }`}>
        {place.michelin_info ? (
          <img src={images.michelinStar} alt="michelinStar" className="w-6 h-6" />
        ) : place.blueribbon_info ? (
          <img src={images.blueRibbon} alt="blueRibbon" className="w-6 h-6" />
        ) : (
          <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
          </svg>
        )}
      </div>
    )
  })) || [];

  return (
    <CenterLayout>
    <div className={`transition-all duration-500 ease-in-out ${isFullscreen ? 'pt-20' : ''}`}>
      <div className={`transition-opacity duration-300 ${isFullscreen ? 'opacity-0 h-0 overflow-hidden' : 'opacity-100'}`}>
        <div className="flex justify-between items-start mb-8">
          <div className=' w-full'>
            <h1 className="text-3xl font-bold text-gray-900 pt-4 mb-4">{t('submenu_restaurants')}</h1>
            <p className="text-gray-600 mb-6">
              {t('restaurants_subtitle')}
            </p>
            <div className="flex flex-col gap-5 bg-gray-50 rounded-xl px-5 py-6 w-full">
              <div className="flex items-center gap-2">
                <img src={images.michelinStar} alt="michelinStar" width={18} height={18} />
                <span className="text-sm text-gray-600">{t('restaurants_michelin_explanation')}</span>
              </div>
              <div className="flex items-center gap-2">
                <img src={images.blueRibbon} alt="blueRibbon" width={18} height={18} />
                <span className="text-sm text-gray-600">{t('restaurants_blueribbon_explanation')}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <FilterTab filters={filtersData} />
      
      {viewType === 'map' && <div className="p-1">
        <div className={`flex flex-col lg:flex-row gap-6 transition-all duration-500 ease-in-out ${
          isFullscreen 
            ? 'fixed inset-0 z-50 bg-white pt-20 px-4' 
            : 'relative'
        }`}>
          {/* 지도 영역 - 왼쪽 */}
          <div className={`${isFullscreen ? 'w-3/4' : 'lg:w-3/4'} transition-all duration-500 ease-in-out`} data-map-container>
            <div className={isFullscreen ? '' : 'sticky top-4'}>
              <div className={`transition-all duration-500 ease-in-out ${
                isFullscreen ? 'h-[calc(100vh-6rem)]' : 'h-[400px] lg:h-[720px]'
              } rounded-lg overflow-hidden shadow-lg`}>
                <NaverMap ref={mapRef} markers={markers} zoom={14}/>
              </div>
            </div>
          </div>
          
          {/* 카드 목록 영역 - 오른쪽 */}
          <div className={`${isFullscreen ? 'w-1/4' : 'lg:w-1/4'} transition-all duration-500 ease-in-out ${
            isFullscreen ? 'h-[calc(100vh-6rem)]' : 'h-[400px] lg:h-[720px]'
          } flex flex-col`}>
            <div className="flex-shrink-0 mb-4">
              <div className="flex justify-between items-center mb-3">
                <h3 className="text-lg font-semibold text-gray-800">{t('restaurants_list')}</h3>
                <div
                  className={`${themeVariables.badge.gray} cursor-pointer hover:bg-gray-100 transition-all duration-300 hidden xl:block`}
                  onClick={toggleFullscreen}
                >
                  <div className="transition-transform duration-200">
                    {isFullscreen ? (
                      <div className="flex items-center gap-1.5">
                        <Minimize2 className="w-3 h-3 transition-transform duration-300" />
                        {t('back')}
                      </div>
                    ) : (
                      <div className="flex items-center gap-1.5">
                        <Maximize2 className="w-3 h-3 transition-transform duration-300" />
                        {t('fullscreen')}
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <p className="text-sm text-gray-600">{t('click_to_view_location')}</p>
            </div>
            
            {/* 레스토랑 정보 카드들 */}
            <div className="flex-1 space-y-4 overflow-y-auto pr-2 transition-all duration-300">
              {restaurants.result.map((place: Restaurant, index: number) => (
                <div 
                  key={place.id}
                  className="p-4 rounded-lg cursor-pointer bg-white border border-gray-200 active:border-gray-300"
                  onClick={() => handleCardClick(index)}
                >
                  <div className="flex items-start gap-3">
                    {/* <div className={`w-3 h-3 rounded-full flex-shrink-0 mt-1 ${
                      place.crowd_level <= 2 ? 'bg-green-500' :
                      place.crowd_level <= 3 ? 'bg-yellow-500' :
                      place.crowd_level <= 4 ? 'bg-orange-500' : 'bg-red-500'
                    }`}></div> */}
                    <div className="flex-1 min-w-0">
                      <div className='flex items-center gap-1.5 mb-2'>
                        <h3 className="font-semibold text-gray-800 text-base truncate">{place.name[language]}</h3>
                        {place.michelin_info && (
                          <MichelinLogos count={place.michelin_info.michelinAward} />
                        )}
                        {place.blueribbon_info && (
                          <BlueRibbonLogos count={place.blueribbon_info.ribbonType} />
                        )}
                      </div>
                      <div className='text-[13px] text-gray-500'>
                        {shortenAddress(place.address?.[language], language)}
                      </div>
                      {/* 미슐랭과 블루리본 로고 표시 */}
                        <div className="flex items-center mt-2 text-gray-600 text-sm">
                         <span key={place.food_category[language]}>
                           {place.food_category[language]}
                         </span>
                         {place.michelin_info && (
                           <>
                             &nbsp;•&nbsp;
                             <PriceRangeBadge priceRange={place.michelin_info.priceSlug} />
                           </>
                         )}
                       </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      }

      {viewType === 'list' && (
        <div className="p-1">
          {/* 레스토랑 그리드 */}
          <div className="grid grid-cols-1 md:grid-cols-3 xl:grid-cols-4 gap-6 gap-y-12">
            {restaurants.result.map((restaurant: Restaurant) => (
              <div key={restaurant.id}>
                <RestaurantCard restaurant={restaurant}/>
              </div>
            ))}
           </div>

           {/* 결과가 없을 때 메시지 */}
            {restaurants.result.length === 0 && !loading && (
              <div className="text-center py-12">
                <p className="text-gray-500 text-lg">{t('no_restaurants_found')}</p>
              </div>
            )}
         </div>
       )}
      </div>
    </CenterLayout>
  );
}

